import React from 'react'
import {Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious} from 'src/components/pagination'
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from 'src/components/select'
import {cn} from 'src/layouts/main-layout/app-sidebar/utils/utils'

interface DataTablePaginationProps<TData> extends React.ComponentProps<'div'> {
  table: TableControl
  pageSizeOptions?: number[]
  siblingCount?: number
  boundaryCount?: number
}
type TableControl = {
  previousPage: () => void
  nextPage: () => void
  pageSize: number
  total: number
  setPageSize: (size: number) => void
  pageIndex: number
  setPageIndex: (index: number) => void
}

type PaginationItem = number | 'ellipsis'

function getPaginationRange(params: {pageIndex: number; pageCount: number; siblingCount: number; boundaryCount: number}): PaginationItem[] {
  const {pageIndex, pageCount, siblingCount, boundaryCount} = params

  if (pageCount <= 0) return []
  const currentPage = pageIndex

  const totalNumbers = boundaryCount * 2 + siblingCount * 2 + 1
  if (pageCount <= totalNumbers) {
    return Array.from({length: pageCount}, (_, i) => i + 1)
  }
  const startPages = Array.from({length: boundaryCount}, (_, i) => i + 1)
  const endPages = Array.from({length: boundaryCount}, (_, i) => pageCount - boundaryCount + 1 + i)

  const siblingsStart = Math.max(boundaryCount + 1, currentPage - siblingCount)
  const siblingsEnd = Math.min(pageCount - boundaryCount, currentPage + siblingCount)

  const showLeftEllipsis = siblingsStart > boundaryCount + 2
  const showRightEllipsis = siblingsEnd < pageCount - boundaryCount - 1

  const items: PaginationItem[] = []

  items.push(...startPages)

  if (showLeftEllipsis) {
    items.push('ellipsis')
  } else {
    for (let p = boundaryCount + 1; p < siblingsStart; p++) items.push(p)
  }

  for (let p = siblingsStart; p <= siblingsEnd; p++) items.push(p)

  if (showRightEllipsis) {
    items.push('ellipsis')
  } else {
    for (let p = siblingsEnd + 1; p <= pageCount - boundaryCount; p++) items.push(p)
  }

  items.push(...endPages)
  return items.filter((item, idx, arr) => arr.indexOf(item) === idx)
}

export function DataTablePagination<TData>({
  table,
  pageSizeOptions = [20, 50, 100],
  siblingCount = 1,
  boundaryCount = 1,
  className,
  ...props
}: DataTablePaginationProps<TData>) {
  const pageIndex = table.pageIndex
  const pageCount = table.total

  const pages = React.useMemo(
    () =>
      getPaginationRange({
        pageIndex,
        pageCount,
        siblingCount,
        boundaryCount,
      }),
    [pageIndex, pageCount, siblingCount, boundaryCount],
  )
  return (
    <div className={cn('flex w-full items-center justify-between gap-4 overflow-auto p-1 sm:flex-row sm:gap-8', className)} {...props}>
      <div className='flex items-center space-x-3'>
        <p className='text-xs font-normal whitespace-nowrap text-[#667085]'>Linhas por página:</p>
        <Select
          value={`${table.pageSize}`}
          onValueChange={(value) => {
            table.setPageIndex(1)
            table.setPageSize(Number(value))
          }}>
          <SelectTrigger className='h-8 w-17.25 data-size:h-8'>
            <SelectValue placeholder={table.pageSize} />
          </SelectTrigger>
          <SelectContent side='top'>
            {pageSizeOptions.map((pageSize) => (
              <SelectItem key={pageSize} value={`${pageSize}`}>
                {pageSize}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Pagination className='m-0 justify-end'>
        <PaginationContent>
          <PaginationItem>
            <PaginationPrevious onClick={() => table.previousPage()} disabled={table.pageIndex === 1} />
          </PaginationItem>
          {pages.map((page, index) => {
            if (page === 'ellipsis') {
              return (
                <PaginationItem key={`ellipsis-${index}`} className='flex size-8 items-center justify-center'>
                  <span className='text-foreground text-xs font-normal'>...</span>
                </PaginationItem>
              )
            }

            const isActive = page === pageIndex

            return (
              <PaginationItem key={page}>
                <PaginationLink isActive={isActive} onClick={() => table.setPageIndex(page)} className='p-2 px-3 text-sm'>
                  {page}
                </PaginationLink>
              </PaginationItem>
            )
          })}
          <PaginationItem>
            <PaginationNext onClick={() => table.nextPage()} disabled={table.total === table.pageIndex} />
          </PaginationItem>
        </PaginationContent>
      </Pagination>
    </div>
  )
}
